import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { errorMessage, apiError, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCirclePlus, CheckCircle, XCircle, SpinnerGap, ShieldCheck, ArrowLeft, ChatCircleText, Clock } from "@phosphor-icons/react";

function NominationCountdown({ deadline, brand, onExpire }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(deadline).getTime() - now;
  useEffect(() => { if (ms <= 0 && onExpire) onExpire(); }, [ms <= 0]); // eslint-disable-line
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const parts = d > 0 ? [`${d}d`, `${h}h`, `${m}m`] : h > 0 ? [`${h}h`, `${m}m`, `${s}s`] : [`${m}m`, `${s}s`];
  const soon = ms < 24 * 3600000;
  return (
    <div data-testid="nom-countdown"
      className={`mb-6 flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border ${soon ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-zinc-50 border-zinc-200 text-zinc-700"}`}>
      <Clock size={16} weight="fill" style={{ color: soon ? "#B45309" : brand }} />
      <span>Nominations close in</span>
      <span className="font-semibold tabular-nums" data-testid="nom-countdown-value">{parts.join(" ")}</span>
    </div>
  );
}

function Shell({ org, children }) {
  const brand = org?.branding?.primary_color || "#1F6FE5";
  return (
    <div className="min-h-[100dvh] bg-[#F7F8F9] grain flex flex-col">
      <header className="backdrop-blur-xl bg-white/80 border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-lg mx-auto px-6 h-14 flex items-center gap-2">
          {org?.branding?.logo_url ? <img src={fileUrl(org.branding.logo_url)} alt="" className="h-7" /> : <div className="w-6 h-6 rounded-[4px]" style={{ background: brand }} />}
          <span className="font-semibold text-sm">{org?.name || "SelectFlow"}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400"><ShieldCheck size={13} weight="fill" style={{ color: brand }} /> Secure</span>
        </div>
      </header>
      <div className="flex-1 max-w-lg w-full mx-auto px-6 py-10 relative z-10">{children}</div>
    </div>
  );
}

function AccessFlow({ id, brand, onBack }) {
  const nav = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const requestPin = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/public/register/${id}/request-access`, { phone });
      setStep("pin");
    } catch (e) { setErr(apiError(e.response?.data?.detail) || "Could not send PIN. Try again."); }
    finally { setBusy(false); }
  };
  const redeem = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/public/register/${id}/redeem-access`, { phone, pin });
      nav(`/vote/${data.token}`);
    } catch (e) { setErr(apiError(e.response?.data?.detail) || "Invalid or expired PIN."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <button onClick={onBack} data-testid="access-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-black transition-colors mb-6"><ArrowLeft size={15} /> Back to registration</button>
      <div className="text-center mb-8">
        <ChatCircleText size={40} weight="duotone" className="mx-auto mb-3" style={{ color: brand }} />
        <h1 className="font-display font-bold text-2xl tracking-tight">Get your voting link</h1>
        <p className="text-zinc-500 mt-1">Already registered? Enter your phone number and we'll text you a one-time PIN.</p>
      </div>
      {err && <div data-testid="access-error" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
      {step === "phone" ? (
        <form onSubmit={requestPin} className="space-y-4 bg-white border border-zinc-200 rounded-xl p-6">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Registered phone number</Label>
            <Input data-testid="access-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" placeholder="0208…" />
          </div>
          <button data-testid="access-request" disabled={busy || !phone} className="w-full h-11 text-white rounded-md font-semibold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60" style={{ background: brand }}>
            {busy && <SpinnerGap size={18} className="animate-spin" />} Send me a PIN
          </button>
          <p className="text-[11px] text-zinc-400">The PIN works once and expires in 15 minutes. Your ballot stays anonymous.</p>
        </form>
      ) : (
        <form onSubmit={redeem} className="space-y-4 bg-white border border-zinc-200 rounded-xl p-6">
          <p className="text-sm text-zinc-500">If that number is registered, a 6-digit PIN was just sent by SMS. Enter it below.</p>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">One-time PIN</Label>
            <Input data-testid="access-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} className="mt-1.5 font-mono text-lg tracking-[0.3em] text-center" placeholder="••••••" inputMode="numeric" />
          </div>
          <button data-testid="access-redeem" disabled={busy || pin.length < 6} className="w-full h-11 text-white rounded-md font-semibold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60" style={{ background: brand }}>
            {busy && <SpinnerGap size={18} className="animate-spin" />} Access my ballot
          </button>
          <button type="button" data-testid="access-resend" onClick={requestPin} disabled={busy} className="w-full text-sm text-zinc-500 hover:text-black transition-colors">Didn't get it? Resend PIN</button>
        </form>
      )}
    </div>
  );
}

export default function Register() {
  const { id } = useParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", station: "" });
  const [custom, setCustom] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [mode, setMode] = useState("register");
  const [payState, setPayState] = useState(null); // verifying | success | failed
  const [nomMode, setNomMode] = useState("self"); // self | others
  const [nominator, setNominator] = useState({ name: "", email: "", phone: "" });
  const [nominees, setNominees] = useState({}); // position -> {name,email,phone}

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("reference") || params.get("trxref");
    if (ref) {
      setPayState("verifying");
      api.get(`/public/nomination/verify/${ref}`)
        .then(({ data }) => setPayState(data.status === "success" ? "success" : "failed"))
        .catch(() => setPayState("failed"));
    }
    api.get(`/public/register/${id}`).then(({ data }) => setInfo(data)).catch(() => setInfo({ allowed: false, reason: "not_enabled" }));
  }, [id]);

  const refetchInfo = () => api.get(`/public/register/${id}`).then(({ data }) => setInfo(data)).catch(() => {});

  const nf = info?.nomination_fee;
  const feeAmount = nf ? (nf.mode === "per_position" ? Number(nf.per_position?.[custom.position_sought] || 0) : Number(nf.amount || 0)) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const callback_url = `${window.location.origin}/register/${id}`;
      const { data } = await api.post(`/public/register/${id}`, { ...form, custom_fields: custom, callback_url });
      if (data.status === "payment_pending" && data.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      setDone(data);
    } catch (err) { /* eslint-disable no-alert */
      const msg = errorMessage(err);
      setForm((f) => ({ ...f }));
      alertBanner(msg);
    } finally { setLoading(false); }
  };
  const [error, setError] = useState("");
  const alertBanner = (m) => setError(m);

  const positions = ((info?.register_fields || []).find((f) => f.key === "position_sought")?.options) || [];

  const submitOthers = async (e) => {
    e.preventDefault();
    setError("");
    const list = positions
      .map((p) => ({ position: p, ...(nominees[p] || {}) }))
      .filter((n) => (n.name || "").trim());
    if (!list.length) { setError("Add at least one nominee name."); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/public/register/${id}/nominate-others`, { nominator, nominees: list });
      setDone({ others: true, count: data.count, pending: data.pending });
    } catch (err) { setError(errorMessage(err)); }
    finally { setLoading(false); }
  };

  if (payState) {
    return (
      <Shell org={info?.org}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-16 text-center" data-testid="nomfee-callback">
          {payState === "verifying" && (<><SpinnerGap size={56} className="animate-spin mx-auto mb-5 text-[#1F6FE5]" /><h1 className="font-display font-bold text-2xl tracking-tight">Confirming payment…</h1><p className="text-zinc-500 mt-1">Please wait, do not close this window.</p></>)}
          {payState === "success" && (<><CheckCircle size={60} weight="fill" className="mx-auto mb-5 text-emerald-500" /><h1 className="font-display font-bold text-2xl tracking-tight mb-2">Nomination submitted!</h1><p className="text-zinc-500">Your payment is confirmed and your nomination is now in for review.</p></>)}
          {payState === "failed" && (<><XCircle size={56} weight="fill" className="mx-auto mb-5 text-red-500" /><h1 className="font-display font-bold text-2xl tracking-tight mb-2">Payment not completed</h1><p className="text-zinc-500 mb-6">We couldn't confirm your payment. You can try registering again.</p><button data-testid="nomfee-retry" onClick={() => { window.location.href = `/register/${id}`; }} className="px-4 py-2.5 bg-black text-white rounded-md text-sm font-semibold">Try again</button></>)}
        </motion.div>
      </Shell>
    );
  }

  if (info === null) return <div className="min-h-[100dvh] flex items-center justify-center text-zinc-400 font-mono text-sm">loading…</div>;

  if (!info.allowed) {
    const map = {
      not_enabled: { t: "Registration unavailable", b: "Self-registration is not enabled for this election." },
      closed: { t: "Registration closed", b: "This election has already closed." },
      nominations_closed: { t: "Nominations closed", b: info.nomination_deadline ? `The deadline for nominations was ${new Date(info.nomination_deadline).toLocaleString()}. No further nominations can be submitted.` : "The nomination period for this election has ended." },
    };
    const m = map[info.reason] || map.not_enabled;
    return (
      <Shell org={info.org}>
        <div className="mt-16 text-center" data-testid="register-closed">
          <XCircle size={56} weight="fill" className="mx-auto mb-5 text-red-500" />
          <h1 className="font-display font-bold text-2xl tracking-tight mb-2">{m.t}</h1>
          <p className="text-zinc-500">{m.b}</p>
        </div>
      </Shell>
    );
  }

  if (info.voting_complete) {
    return (
      <Shell org={info.org}>
        <div className="mt-16 text-center" data-testid="voting-complete">
          <CheckCircle size={60} weight="fill" className="mx-auto mb-5 text-emerald-500" />
          <h1 className="font-display font-bold text-2xl tracking-tight mb-2">Voting is now closed</h1>
          <p className="text-zinc-500">All validated votes are in — voting is now closed.</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell org={info.org}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-16 text-center">
          <CheckCircle size={60} weight="fill" className="mx-auto mb-5 text-emerald-500" />
          <h1 className="font-display font-bold text-2xl tracking-tight mb-2">{done.others ? "Nominations submitted!" : info.nomination_for ? (done.pending ? "Nomination received!" : "Nomination submitted!") : (done.pending ? "Registration received!" : "You're registered!")}</h1>
          {done.paid && <p className="text-xs text-emerald-600 font-medium mb-2">Payment received ✓</p>}
          <p className="text-zinc-500">
            {done.others
              ? `Thanks! You've submitted ${done.count} nomination${done.count === 1 ? "" : "s"}.${done.pending ? " An organiser will review them." : ""}`
              : info.nomination_for
              ? (done.pending
                  ? "Thanks! An organiser will review your nomination."
                  : "Thanks! Your nomination has been submitted.")
              : done.pending
              ? "An organiser will review your registration. Once approved, your unique voting link will be sent to you."
              : done.invited
              ? "Check your email or phone — your unique voting link is on its way."
              : "You've been added to the voter roll. Your voting link will be sent when the election opens."}
          </p>
        </motion.div>
      </Shell>
    );
  }

  return (
    <Shell org={info.org}>
      {mode === "access" ? (
        <AccessFlow id={id} brand={info.org?.branding?.primary_color || "#1F6FE5"} onBack={() => setMode("register")} />
      ) : (
      <>
      <div className="text-center mb-8">
        <UserCirclePlus size={40} weight="duotone" className="mx-auto text-[#1F6FE5] mb-3" />
        <h1 className="font-display font-bold text-2xl tracking-tight">{info.nomination_for ? (nomMode === "others" ? "Nominate others" : "Submit a nomination") : "Register to vote"}</h1>
        <p className="text-zinc-500 mt-1">{info.election_title}</p>
      </div>
      {info.nomination_for && info.nomination_deadline && (
        <NominationCountdown deadline={info.nomination_deadline} brand={info.org?.branding?.primary_color || "#1F6FE5"} onExpire={refetchInfo} />
      )}
      {info.nomination_for && (
        <div className="flex gap-1 mb-6 bg-zinc-100 p-1 rounded-lg" data-testid="nom-mode-toggle">
          <button type="button" data-testid="nom-mode-self" onClick={() => { setNomMode("self"); setError(""); }} className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${nomMode === "self" ? "bg-white shadow-sm text-black" : "text-zinc-500 hover:text-zinc-700"}`}>Nominate myself</button>
          <button type="button" data-testid="nom-mode-others" onClick={() => { setNomMode("others"); setError(""); }} className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${nomMode === "others" ? "bg-white shadow-sm text-black" : "text-zinc-500 hover:text-zinc-700"}`}>Nominate others</button>
        </div>
      )}
      {error && <div data-testid="reg-error" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
      {info.registration_full && (
        <div data-testid="registration-full" className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5">
          {info.nomination_for ? "Nominations are full — the maximum number of entries has been reached." : "Registration is full — the maximum number of voters has been reached."}
        </div>
      )}
      {info.nomination_for && nomMode === "others" ? (
      <form onSubmit={submitOthers} className="space-y-5 bg-white border border-zinc-200 rounded-xl p-6" data-testid="nominate-others-form">
        <p className="text-sm text-zinc-600">Add one nominee for each position you'd like to fill. Leave a position blank to skip it.</p>
        <div className="space-y-4">
          {positions.length === 0 && <p className="text-sm text-amber-600">No positions have been set up for this nomination yet.</p>}
          {positions.map((p) => {
            const val = nominees[p] || {};
            const setVal = (patch) => setNominees((s) => ({ ...s, [p]: { ...(s[p] || {}), ...patch } }));
            return (
              <div key={p} className="border border-zinc-200 rounded-lg p-4 space-y-3" data-testid={`nom-pos-${p}`}>
                <Label className="text-xs uppercase tracking-widest font-semibold" style={{ color: info.org?.branding?.primary_color || "#1F6FE5" }}>{p}</Label>
                <Input data-testid={`nom-name-${p}`} placeholder="Nominee full name" value={val.name || ""} onChange={(e) => setVal({ name: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid={`nom-email-${p}`} type="email" placeholder="Email (optional)" value={val.email || ""} onChange={(e) => setVal({ email: e.target.value })} />
                  <Input data-testid={`nom-phone-${p}`} placeholder="Phone (optional)" value={val.phone || ""} onChange={(e) => setVal({ phone: e.target.value })} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-zinc-100 pt-4 space-y-3">
          <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Your details (optional)</p>
          <Input data-testid="nominator-name" placeholder="Your name (optional)" value={nominator.name} onChange={(e) => setNominator({ ...nominator, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="nominator-email" type="email" placeholder="Your email (optional)" value={nominator.email} onChange={(e) => setNominator({ ...nominator, email: e.target.value })} />
            <Input data-testid="nominator-phone" placeholder="Your phone (optional)" value={nominator.phone} onChange={(e) => setNominator({ ...nominator, phone: e.target.value })} />
          </div>
        </div>
        <button data-testid="nominate-others-submit" disabled={loading || info.registration_full || positions.length === 0} className="w-full h-11 text-white rounded-md font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: info.org?.branding?.primary_color || "#1F6FE5" }}>
          {loading && <SpinnerGap size={18} className="animate-spin" />} {info.registration_full ? "Nominations full" : "Submit nominations"}
        </button>
        <p className="text-[11px] leading-relaxed text-zinc-400 border-t border-zinc-100 pt-3">Nominating others is free. Nominees may be contacted by the organiser to confirm they accept the nomination.</p>
      </form>
      ) : (
      <form onSubmit={submit} className="space-y-4 bg-white border border-zinc-200 rounded-xl p-6">
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Full name</Label>
          <Input data-testid="reg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Email{info.nomination_for ? " (optional)" : ""}</Label>
          <Input data-testid="reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="you@example.com" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Phone{info.nomination_for ? " (optional)" : ""}</Label>
          <Input data-testid="reg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="0208…" />
        </div>
        {!info.nomination_for && (
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Polling station <span className="normal-case text-zinc-300">(optional)</span></Label>
            <Input data-testid="reg-station" value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} className="mt-1.5" placeholder="e.g. North Hall" />
          </div>
        )}
        <p className="text-xs text-zinc-400">{info.nomination_for ? "Add an email or phone if you'd like to receive updates — optional." : "Enter an email or a phone number (or both) so we can send your voting link."}</p>
        {(info.register_fields || []).map((cf) => {
          const t = cf.type || "text";
          if (t === "checkbox") {
            return (
              <label key={cf.key} className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" data-testid={`reg-cf-${cf.key}`} checked={!!custom[cf.key]} onChange={(e) => setCustom({ ...custom, [cf.key]: e.target.checked })} className="mt-1 accent-[#1F6FE5]" />
                <span className="text-sm text-zinc-600">{cf.label}{cf.required ? " *" : ""}</span>
              </label>
            );
          }
          return (
            <div key={cf.key}>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">{cf.label}{cf.required ? " *" : ""}</Label>
              {t === "dropdown" ? (
                <select data-testid={`reg-cf-${cf.key}`} value={custom[cf.key] || ""} onChange={(e) => setCustom({ ...custom, [cf.key]: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                  <option value="">Select…</option>
                  {(cf.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input data-testid={`reg-cf-${cf.key}`} type={t === "number" ? "number" : "text"} value={custom[cf.key] || ""} onChange={(e) => setCustom({ ...custom, [cf.key]: e.target.value })} className="mt-1.5" />
              )}
            </div>
          );
        })}
        {info.requires_approval && <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">An organiser will review and approve your registration before sending your voting link.</p>}
        {feeAmount > 0 && (
          <div data-testid="reg-fee-banner" className="text-sm text-zinc-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2.5 flex items-center gap-2">
            <span className="font-semibold" style={{ color: info.org?.branding?.primary_color || "#1F6FE5" }}>{nf.symbol}{feeAmount.toFixed(2)}</span>
            <span>nomination fee — you'll be taken to secure checkout to pay.</span>
          </div>
        )}
        <button data-testid="reg-submit" disabled={loading || info.registration_full} className="w-full h-11 text-white rounded-md font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: info.org?.branding?.primary_color || "#1F6FE5" }}>
          {loading && <SpinnerGap size={18} className="animate-spin" />} {info.registration_full ? (info.nomination_for ? "Nominations full" : "Registration full") : feeAmount > 0 ? `Continue to payment · ${nf.symbol}${feeAmount.toFixed(2)}` : info.nomination_for ? "Submit nomination" : "Register"}
        </button>
        <p data-testid="reg-privacy" className="text-[11px] leading-relaxed text-zinc-400 border-t border-zinc-100 pt-3">
          <strong className="text-zinc-500">Privacy:</strong> {info.org?.name || "The organiser"} is the data controller; SelectFlow processes your details on their behalf solely to run this {info.nomination_for ? "nomination" : "election"}. {info.nomination_for ? "We collect your name and role, plus any optional contact details you choose to share." : "We collect your name and contact details to send your unique voting link."} Your data is deleted after the {info.nomination_for ? "nomination" : "election"} closes on the organiser's instruction. To request erasure, contact the organiser. Ballots are anonymous and never linked to your identity.
        </p>
      </form>
      )}
      {info.status === "live" && !info.nomination_for && (
        <div className="text-center mt-5">
          <button data-testid="access-switch" onClick={() => setMode("access")} className="text-sm font-medium text-zinc-500 hover:text-black transition-colors">
            Already registered? <span className="underline" style={{ color: info.org?.branding?.primary_color || "#1F6FE5" }}>Get your voting link</span>
          </button>
        </div>
      )}
      </>
      )}
    </Shell>
  );
}
